import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useSelector } from 'react-redux';

import requestLocation from '../../../components/hocs/RequestLocation';
import useClickOutsideRef from '../../../components/hooks/useClickOutsideRef';
import { userIsFreeOrProCollegeClubOwner } from '../../../utils/user';
import ConferenceFilter from '../../SearchPage/Filters/ConferenceFilter';
import DivisionFilter from '../../SearchPage/Filters/DivisionFilter';
import GenderFilter from '../../SearchPage/Filters/GenderFilter';
import HSGradYearFilter from '../../SearchPage/Filters/HSGradYearFilter';
import LocationFilter from '../../SearchPage/Filters/LocationFilter';
import NationalityFilter from '../../SearchPage/Filters/NationalityFilter';
import SnapshotDateFilter from '../../SearchPage/Filters/SnapshotDateFilter';
import UtrFilter from '../../SearchPage/Filters/UtrFilter';
import CollegeCornerMobileFilters from './CollegeCornerMobileFilters';
import CollegeFitToolFilter from './CollegeFitToolFilter';
import RecruitingStatusFilter from './RecruitingStatusFilter';

import styles from './CollegeCornerFilters.scss';

import { PowerFunnelSurfaces } from '../../../components/Analytics/constants';
import { getGatedFeatureForFilter } from '../../../components/Analytics/helpers';
import useStartPowerCheckout from '../../../components/hooks/useStartPowerCheckout';

const rankings = 'rankings';

function CollegeCornerFilters({
    mobileFilter,
    tempFilters,
    defaultFilters,
    onhandleUpdateFilters,
    onhandleFilterSubmit,
    filterOptions,
    sortingOptions,
    onhandleResetFilters,
    fetchCurrentLocation,
    customOptions,
    page,
    showGradYearFilter,
}) {
    const filterWrapper = useRef(null);
    const auth = useSelector((state) => state.auth);
    const coreUI = useSelector((state) => state.coreUI);
    const baseSearchTypes = coreUI?.types?.search ? coreUI.types.search : null;
    const searchTypes = baseSearchTypes;
    const userLoggedIn = auth?.user || false;
    const isPowerUser = page === rankings ? true : userLoggedIn && auth.user.isPowered;
    const [filters, setFilters] = useState({ locationInputValue: '' });
    const [filterModal, setFilterModal] = useState('');
    const { startPowerCheckout } = useStartPowerCheckout();
    const powerFilterList = ['conference', 'division', 'nationality', 'snapshotDate'];

    useClickOutsideRef(filterWrapper, () => setFilterModal(''));

    useEffect(() => {
        if (defaultFilters) {
            setFilters(defaultFilters);
        }
    }, [defaultFilters]);

    const clearLocationInput = () => {
        if (!tempFilters?.location && tempFilters?.locationInputValue) {
            onhandleUpdateFilters({
                locationInputValue: '',
            });
        }
    };

    useEffect(() => {
        if (!filterModal) {
            clearLocationInput();
        }
    }, [filterModal]);

    const handlePowerPopup = (type) => {
        startPowerCheckout({
            surface: PowerFunnelSurfaces.COLLEGE_CORNER,
            gatedFeature: getGatedFeatureForFilter(type),
            confirmResume: true,
        });
    };

    const validateFilterAvailablity = (filterName) => {
        return !isPowerUser && powerFilterList.indexOf(filterName) > -1;
    };

    const handleFilterClick = (type) => {
        if (validateFilterAvailablity(type)) {
            handlePowerPopup(type);
        } else {
            setFilterModal((prevState) => (prevState === type ? '' : type));
        }
    };

    useEffect(() => {
        if (mobileFilter) {
            setFilterModal(mobileFilter);
        }
    }, [mobileFilter]);

    const closeModals = () => {
        setFilterModal('');
    };

    const handleLocationChange = (locationInputValue) => {
        onhandleUpdateFilters({
            locationInputValue,
            locationCountryName: '',
            locationStateName: '',
            location: '',
            lat: null,
            lng: null,
        });
    };

    const handleFilterUpdate = (props, update) => {
        if (!update) {
            onhandleUpdateFilters({ ...props });
            return;
        }
        if (update === 'submit') {
            onhandleFilterSubmit({ ...props });
            closeModals();
        }
    };

    const handleLocationSelect = (selectedLocation) => {
        if (selectedLocation) {
            geocodeByAddress(selectedLocation)
                .then(async (results) => {
                    const latLng = await getLatLng(results[0]);
                    return { results, latLng };
                })
                .then(({ results, latLng }) => {
                    if (results[0].types[0] === 'country') {
                        handleFilterUpdate({
                            locationCountryName: results[0].address_components[0].long_name,
                            locationStateName: '',
                            distance: filters.distance || '50mi',
                            location: '',
                            locationInputValue: selectedLocation || '',
                            lat: null,
                            lng: null,
                        });
                    } else if (results[0].types[0] === 'administrative_area_level_1') {
                        handleFilterUpdate({
                            locationCountryName: results[0].address_components[1].long_name,
                            locationStateName: results[0].address_components[0].long_name,
                            distance: filters.distance || '50mi',
                            location: '',
                            locationInputValue: selectedLocation || '',
                            lat: latLng.lat,
                            lng: latLng.lng,
                        });
                    } else {
                        handleFilterUpdate({
                            lat: latLng.lat,
                            lng: latLng.lng,
                            location: selectedLocation,
                            distance: filters.distance || '50mi',
                            locationInputValue: selectedLocation || '',
                            locationCountryName: '',
                            locationStateName: '',
                        });
                    }
                })
                .catch((error) => console.error('geocodeByAddress error', error));
        }
    };

    const handleFilterSubmit = (props) => {
        closeModals();
        onhandleFilterSubmit(props);
    };

    const onLocationRecieved = (location) => {
        onhandleFilterSubmit({
            lat: location.lat,
            lng: location.lng,
            location: location.location,
            locationInputValue: location.location || '',
            locationCountryName: '',
            locationStateName: '',
            distance: filters.distance || '50mi',
        });
        closeModals();
    };

    const handleCurrentLocationClick = () => {
        if (typeof fetchCurrentLocation === 'function') {
            fetchCurrentLocation(onLocationRecieved);
        }
    };

    const handleDistanceChange = (distance) => {
        onhandleUpdateFilters({
            distance: `${distance}mi`,
            locationCountryName: '',
            locationStateName: '',
        });
    };

    const applyLocationFilter = () => {
        const {
            locationInputValue,
            location,
            lat,
            lng,
            distance,
            locationCountryName,
            locationStateName,
        } = tempFilters?.locationInputValue ? tempFilters : filters;
        if (
            !(
                (location && lat && lng && distance) ||
                (locationCountryName || locationStateName)
            )
        ) {
            geocodeByAddress(locationInputValue)
                .then(async (results) => {
                    const latLng = await getLatLng(results[0]);
                    return { results, latLng };
                })
                .then(({ results, latLng }) => {
                    if (results[0].types[0] === 'country') {
                        handleFilterUpdate({
                            locationCountryName: results[0].address_components[0].long_name,
                            locationStateName: '',
                            distance: filters.distance || '50mi',
                            location: '',
                            locationInputValue: locationInputValue || '',
                            lat: null,
                            lng: null,
                        });
                    } else if (results[0].types[0] === 'administrative_area_level_1') {
                        handleFilterUpdate({
                            locationCountryName: results[0].address_components[1].long_name,
                            locationStateName: results[0].address_components[0].long_name,
                            distance: filters.distance || '50mi',
                            location: '',
                            locationInputValue: locationInputValue || '',
                            lat: null,
                            lng: null,
                        });
                    } else {
                        handleFilterUpdate({
                            lat: latLng.lat,
                            lng: latLng.lng,
                            location: locationInputValue,
                            distance: filters.distance || '50mi',
                            locationInputValue: locationInputValue || '',
                            locationCountryName: '',
                            locationStateName: '',
                        });
                    }
                    handleFilterSubmit();
                })
                .catch((error) => console.error('geocodeByAddress error', error));
        } else {
            handleFilterSubmit();
        }
    };

    const onhandleClearFields = () => {
        closeModals();
        onhandleResetFilters();
    };

    const handleHSGradYearClick = (hsdSelectedvalue) => {
        const gradYears = tempFilters?.hsGradYears ? tempFilters.hsGradYears : filters.hsGradYears;
        const hsGradYearsToApply = {
            ...gradYears,
            [hsdSelectedvalue]: !gradYears[hsdSelectedvalue],
        };

        onhandleUpdateFilters({
            hsGradYears: hsGradYearsToApply,
        });
    };

    const clearLocationFilters = () => {
        onhandleFilterSubmit({
            locationInputValue: '',
            locationCountryName: '',
            locationStateName: '',
            distance: '50mi',
            location: '',
            lat: null,
            lng: null,
        });
        closeModals();
    };

    const commonFilterProps = {
        filters,
        filterInputs: { ...filters, ...tempFilters },
        handleFilterClick,
        filterModal,
        closeModals,
        handleFilterSubmit,
        clearFilters: onhandleClearFields,
        handleFilterUpdate,
        type: 'collegeCorner',
        applyLocationFilter,
        handleLocationChange,
        handleLocationSelect,
        handleCurrentLocationClick,
        handleDistanceChange,
    };

    return (
        <div ref={filterWrapper} className={`${styles.CollegeCornerFilters}`}>
            <CollegeCornerMobileFilters
                filters={filters}
                toggleModal={handleFilterClick}
                onClear={onhandleClearFields}
                onApply={handleFilterSubmit}
                filterOptions={filterOptions}
                sortingOptions={sortingOptions}
                searchTypes={searchTypes}
                validateFilters={userLoggedIn && userIsFreeOrProCollegeClubOwner(userLoggedIn)}
                customOptions={customOptions}
                data-testid="college-corner.mobile-filters"
            />
            {filterOptions.indexOf('gender') > -1 && (
                <GenderFilter
                    {...commonFilterProps}
                    customOptions={customOptions?.gender}
                    data-testid="college-corner.filter.gender"
                />
            )}
            {filterOptions.indexOf('utr') > -1 && userLoggedIn && (
                <UtrFilter
                    {...commonFilterProps}
                    showExpandedUtrFilter={false}
                    desktopOnly={true}
                    data-testid="college-corner.filter.utr"
                />
            )}
            {filterOptions.indexOf('highSchool') > -1 &&
                (userLoggedIn && showGradYearFilter) && (
                    <HSGradYearFilter
                        {...commonFilterProps}
                        searchTypes={searchTypes}
                        handleHSGradYearClick={handleHSGradYearClick}
                        data-testid="college-corner.filter.hs-grad-year"
                    />
            )}
            {filterOptions.indexOf('recruitingStatus') > -1 && userLoggedIn && (
                <RecruitingStatusFilter
                    {...commonFilterProps}
                    desktopOnly={true}
                    data-testid="college-corner.filter.recruiting-status"
                />
            )}
            {filterOptions.indexOf('location') > -1 && (
                <LocationFilter
                    {...commonFilterProps}
                    clearFilters={clearLocationFilters}
                    geocoded={false}
                    applyLocationFilter={applyLocationFilter}
                    data-testid="college-corner.filter.location"
                />
            )}

            {filterOptions.indexOf('division') > -1 && (userLoggedIn || page === 'rankings') && (
                <DivisionFilter
                    {...commonFilterProps}
                    searchTypes={searchTypes}
                    desktopOnly={true}
                    customOptions={customOptions}
                    placeholder={page === rankings ? '' : 'Any Division'}
                    data-testid="college-corner.filter.division"
                />
            )}
            {filterOptions.indexOf('conference') > -1 && userLoggedIn && (
                <ConferenceFilter
                    {...commonFilterProps}
                    searchTypes={searchTypes}
                    desktopOnly={true}
                    data-testid="college-corner.filter.conference"
                />
            )}
            {filterOptions.indexOf('nationality') > -1 && userLoggedIn && (
                <NationalityFilter
                    {...commonFilterProps}
                    searchTypes={searchTypes}
                    desktopOnly={true}
                    data-testid="college-corner.filter.nationality"
                />
            )}
            {filterOptions.indexOf('snapshotDate') > -1 && (userLoggedIn || page === 'rankings') && (
                <SnapshotDateFilter
                    {...commonFilterProps}
                    snapshotDates={customOptions.snapshotDates}
                    desktopOnly={true}
                    data-testid="college-corner.filter.snapshot-date"
                />
            )}
            {filterOptions.indexOf('utrFit') > -1 && (
                <CollegeFitToolFilter
                    {...commonFilterProps}
                    desktopOnly={true}
                    data-testid="college-corner.filter.college-fit"
                />
            )}
            <span
                className={`clickable text-teal text-small ${styles.resetFilter}`}
                onClick={onhandleClearFields}
                data-testid="college-corner.reset-filters-link"
            >
                Reset filters
            </span>
        </div>
    );
}

CollegeCornerFilters.defaultProps = {
    tempFilters: {},
    customOptions: null,
    defaultFilters: {},
    sortingOptions: [],
    filterOptions: [],
    isPoweredPopupOpen: false,
    togglePoweredPop: () => { },
    page: null,
    showGradYearFilter: false,
};

CollegeCornerFilters.propTypes = {
    tempFilters: PropTypes.objectOf(PropTypes.shape),
    defaultFilters: PropTypes.objectOf(PropTypes.shape),
    onhandleUpdateFilters: PropTypes.func.isRequired,
    onhandleFilterSubmit: PropTypes.func.isRequired,
    onhandleResetFilters: PropTypes.func.isRequired,
    filterOptions: PropTypes.arrayOf(PropTypes.shape),
    sortingOptions: PropTypes.arrayOf(PropTypes.shape),
    togglePoweredPop: PropTypes.func,
    isPoweredPopupOpen: PropTypes.bool,
    customOptions: PropTypes.objectOf(PropTypes.shape),
    page: PropTypes.string,
    showGradYearFilter: PropTypes.bool,
};

export default requestLocation(CollegeCornerFilters);
